import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  IoCalendar,
  IoCalendarOutline,
  IoDownload,
  IoDownloadOutline,
  IoImages,
  IoImagesOutline,
  IoMail,
  IoMailOutline,
  IoMegaphone,
  IoMegaphoneOutline,
  IoNotifications,
  IoNotificationsOutline,
  IoPeople,
  IoPeopleOutline,
  IoPerson,
  IoPersonOutline,
  IoSettings,
  IoSettingsOutline,
  IoStatsChart,
  IoStatsChartOutline,
} from 'react-icons/io5';
import './BottomNav.css';
import { normalizeAgeType } from '../utils/ageType';

// Acciones del panel administrativo (solo Administrador, userRank === 3).
// Incluye crear encuesta para los administradores. El Vicepresidente NO ve
// esta fila — solo accede al panel de encuestas (gestión).
const ADMIN_PANEL_ITEMS = [
  {
    key: 'a_users',
    to: '/admin/administracion-usuarios',
    label: 'Usuarios',
    icon: { off: IoPeopleOutline, on: IoPeople },
  },
  {
    key: 'a_noti',
    to: '/admin/crear-notificacion',
    label: 'Crear notificación',
    icon: { off: IoNotificationsOutline, on: IoNotifications },
  },
  {
    key: 'a_enc',
    to: '/admin/crear-encuesta',
    label: 'Crear encuesta',
    icon: { off: IoStatsChartOutline, on: IoStatsChart },
  },
  {
    key: 'a_img',
    to: '/admin/subida-de-imagenes',
    label: 'Imágenes',
    icon: { off: IoImagesOutline, on: IoImages },
  },
  {
    key: 'a_msg',
    to: '/admin/mensajes',
    label: 'Mensajes',
    icon: { off: IoMailOutline, on: IoMail },
  },
  {
    key: 'a_stats',
    to: '/admin/estadisticas',
    label: 'Estadísticas',
    icon: { off: IoStatsChartOutline, on: IoStatsChart },
  },
];

// Panel de gestión de encuestas. Visible para Vicepresidente (rank 2) y
// Administrador (rank 3). Idéntico para ambos roles: Encuestas + Gestionar.
const SURVEY_PANEL_ITEMS = [
  {
    key: 's_surveys',
    to: '/contestar-encuesta',
    label: 'Encuestas',
    icon: { off: IoStatsChartOutline, on: IoStatsChart },
  },
  {
    key: 's_manage',
    to: '/gestionar-encuestas',
    label: 'Gestionar encuestas',
    icon: { off: IoStatsChartOutline, on: IoStatsChart },
  },
];

const FALLERO_VISIBLE_PREFIXES = [
  '/perfil',
  '/editar-perfil',
  '/notificaciones',
  '/contestar-encuesta',
  '/publicidad',
  '/descargas',
  '/calendario-rodrigo',
  '/descuentos',
  '/personal',
  '/personal-stats',
  '/add-user',
  '/valid-qr',
  '/pago-ok',
  '/pago-ko',
  '/pago-redsys',
  '/admin',
  '/crear-usuario',
  '/editar-usuario',
  '/editar-notificacion',
  '/editar-encuesta',
  '/gestionar-encuestas',
];

// Rutas del panel de Administración estricto (solo rank 3). El editar/crear
// de encuestas se ha sacado fuera porque también es accesible por Vicepresidente
// (rank 2) y la "bubble" del nav debe seguir resaltando "Encuestas", no "Admin".
const ADMIN_ROUTE_PREFIXES = [
  '/admin/administracion-usuarios',
  '/admin/crear-notificacion',
  '/admin/subida-de-imagenes',
  '/admin/mensajes',
  '/admin/estadisticas',
  '/admin/patrocinadores',
  '/crear-usuario',
  '/editar-usuario',
  '/editar-notificacion',
];

const SURVEY_ROUTE_PREFIXES = [
  '/contestar-encuesta',
  '/gestionar-encuestas',
  '/editar-encuesta',
];

const pathStartsWith = (pathname, prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`);

export const BottomNav = ({ I18n, user }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const pathname = location.pathname;
  const userRank = Number(user?.userRank ?? 0);
  // Admin estricto (rank 3): puede entrar al panel /admin (usuarios, etc).
  const isAdmin = userRank === 3;
  // Gestor de encuestas (rank 2 o 3): ve el panel secundario de encuestas
  // y puede acceder a /gestionar-encuestas + /admin/crear-encuesta.
  const isPollManager = userRank >= 2;
  const isTemporaryUser = normalizeAgeType(user?.ageType) === 4;

  const shouldShowBottomNav = useMemo(() => (
    Boolean(user) && FALLERO_VISIBLE_PREFIXES.some((prefix) => pathStartsWith(pathname, prefix))
  ), [pathname, user]);

  const isInAdminRoute = useMemo(() => (
    isAdmin && ADMIN_ROUTE_PREFIXES.some((prefix) => pathStartsWith(pathname, prefix))
  ), [isAdmin, pathname]);

  const isInSurveyRoute = useMemo(() => (
    SURVEY_ROUTE_PREFIXES.some((prefix) => pathStartsWith(pathname, prefix))
  ), [pathname]);

  const tabs = useMemo(() => {
    const base = [
      {
        key: 'perfil',
        to: '/perfil',
        label: I18n?.Profile || 'Perfil',
        icon: { off: IoPersonOutline, on: IoPerson },
      },
      {
        key: 'notificaciones',
        to: '/notificaciones',
        label: I18n?.Notifications || 'Notificaciones',
        icon: { off: IoNotificationsOutline, on: IoNotifications },
      },
      {
        key: 'encuestas',
        to: '/contestar-encuesta',
        label: I18n?.Surveys || 'Encuestas',
        icon: { off: IoStatsChartOutline, on: IoStatsChart },
      },
      {
        key: 'publicidad',
        to: '/publicidad',
        label: I18n?.Publicidad || 'Publicidad',
        icon: { off: IoMegaphoneOutline, on: IoMegaphone },
      },
      {
        key: 'descargas',
        to: '/descargas',
        label: I18n?.Downloads || 'Descargas',
        icon: { off: IoDownloadOutline, on: IoDownload },
      },
      {
        key: 'calendario',
        to: '/calendario-rodrigo',
        label: I18n?.Calendar || 'Calendario',
        icon: { off: IoCalendarOutline, on: IoCalendar },
      },
    ];

    if (isAdmin) {
      base.push({
        key: 'admin',
        to: '/admin/administracion-usuarios',
        label: I18n?.Administration || 'Admin',
        icon: { off: IoSettingsOutline, on: IoSettings },
      });
    }

    return isTemporaryUser
      ? base.filter((tab) => tab.key !== 'descargas')
      : base;
  }, [I18n, isAdmin, isTemporaryUser]);

  const activeIndex = useMemo(() => {
    if (isInAdminRoute) {
      return tabs.findIndex((tab) => tab.key === 'admin');
    }

    if (isInSurveyRoute) {
      return tabs.findIndex((tab) => tab.key === 'encuestas');
    }

    const idx = tabs.findIndex((tab) => pathStartsWith(pathname, tab.to));
    return idx >= 0 ? idx : 0;
  }, [isInAdminRoute, isInSurveyRoute, pathname, tabs]);

  const [adminOpen, setAdminOpen] = useState(false);
  const [surveysOpen, setSurveysOpen] = useState(isPollManager && isInSurveyRoute);
  const previousSurveyRouteRef = useRef(isInSurveyRoute);
  const [bubblePreviewIndex, setBubblePreviewIndex] = useState(null);

  useEffect(() => {
    setAdminOpen(false);
  }, [pathname]);

  useEffect(() => {
    const wasInSurveyRoute = previousSurveyRouteRef.current;

    // El panel de encuestas (gestionar / crear) lo ven Vicepresidente y Admin.
    if (!isPollManager) {
      setSurveysOpen(false);
    } else if (isInSurveyRoute && !wasInSurveyRoute) {
      setSurveysOpen(true);
    } else if (!isInSurveyRoute) {
      setSurveysOpen(false);
    }

    previousSurveyRouteRef.current = isInSurveyRoute;
  }, [isPollManager, isInSurveyRoute]);

  const secondaryPanelVisible = adminOpen || surveysOpen;
  const secondaryPanelItems = adminOpen ? ADMIN_PANEL_ITEMS : SURVEY_PANEL_ITEMS;
  const secondaryPanelVariant = adminOpen ? 'admin' : 'surveys';
  const renderedBubbleIndex = bubblePreviewIndex ?? activeIndex;

  useLayoutEffect(() => {
    if (!shouldShowBottomNav) {
      document.body.classList.remove('has-bottomnav');
      document.body.classList.remove('has-bottomnav-secondary');
      return undefined;
    }

    document.body.classList.add('has-bottomnav');
    document.body.classList.toggle('has-bottomnav-secondary', secondaryPanelVisible);

    return () => {
      document.body.classList.remove('has-bottomnav');
      document.body.classList.remove('has-bottomnav-secondary');
    };
  }, [secondaryPanelVisible, shouldShowBottomNav]);

  const barRef = useRef(null);
  const tabRefs = useRef([]);
  const [bubbleX, setBubbleX] = useState(0);

  useLayoutEffect(() => {
    const measureBubble = () => {
      const bar = barRef.current;
      const activeTab = tabRefs.current[renderedBubbleIndex];
      if (!bar || !activeTab) return;

      const barRect = bar.getBoundingClientRect();
      const tabRect = activeTab.getBoundingClientRect();
      const centerX = tabRect.left - barRect.left + (tabRect.width / 2);
      setBubbleX(centerX);
    };

    measureBubble();
    window.addEventListener('resize', measureBubble);
    return () => window.removeEventListener('resize', measureBubble);
  }, [renderedBubbleIndex, tabs.length, shouldShowBottomNav]);

  useEffect(() => {
    if (!adminOpen) {
      setBubblePreviewIndex(null);
    }
  }, [adminOpen]);

  if (!shouldShowBottomNav) {
    return null;
  }

  const childRouteActive = (to) => pathStartsWith(pathname, to);

  const handleTabAction = (tab) => {
    if (tab.key === 'admin') {
      setSurveysOpen(false);
      setAdminOpen((open) => {
        const nextOpen = !open;
        setBubblePreviewIndex(nextOpen ? tabs.findIndex((item) => item.key === 'admin') : null);
        return nextOpen;
      });
      return;
    }

    if (tab.key === 'encuestas' && isPollManager) {
      setAdminOpen(false);

      if (isInSurveyRoute) {
        setSurveysOpen((open) => !open);
      } else {
        setSurveysOpen(true);
        navigate('/contestar-encuesta');
      }
      return;
    }

    setSurveysOpen(false);
    setBubblePreviewIndex(null);
    navigate(tab.to);
  };

  return (
    <nav className={`lg3-tabbar count-${tabs.length} ${secondaryPanelVisible ? 'is-admin-open' : ''}`}>
      {/*
        El panel secundario lo ve cualquier "gestor de encuestas" (rank ≥ 2):
        - Admin (3): puede abrir tanto el panel ADMIN (usuarios, etc.) como el
          de encuestas.
        - Vicepresidente (2): solo abre el panel de encuestas; el panel admin
          nunca aparece porque la pestaña "admin" no se le pinta arriba.
        Si no es ni Admin ni Vicepresidente, no hay nada que mostrar.
      */}
      {isPollManager && (
        <div
          className={`lg3-adminrow lg3-secondaryrow lg3-secondaryrow-${secondaryPanelVariant} ${secondaryPanelVisible ? 'show' : ''}`}
          aria-hidden={!secondaryPanelVisible}
        >
          <div className="lg3-divider" />
          <div className={`lg3-adminitems count-${secondaryPanelItems.length}`}>
            {secondaryPanelItems.map((tab) => {
              const isActive = childRouteActive(tab.to);
              const Icon = isActive ? tab.icon.on : tab.icon.off;

              return (
                <button
                  key={tab.key}
                  className={`lg3-adminbtn ${isActive ? 'is-active' : ''}`}
                  type="button"
                  onClick={() => navigate(tab.to)}
                >
                  <Icon className="lg3-adminico" />
                  <span className="lg3-adminlabel">{tab.label}</span>
                </button>
              );
            })}
          </div>
        </div>
      )}

      <div
        ref={barRef}
        className="lg3-mainrow"
        role="navigation"
        aria-label="Bottom Navigation"
      >
        <div
          className="lg3-bubble"
          style={{ transform: `translateX(${bubbleX}px) translateX(-50%) translateY(-50%)` }}
          aria-hidden="true"
        />

        {tabs.map((tab, index) => {
          const isActive = index === activeIndex;
          const Icon = isActive ? tab.icon.on : tab.icon.off;

          return (
            <button
              key={tab.key}
              ref={(element) => {
                tabRefs.current[index] = element;
              }}
              className={`lg3-tab ${isActive ? 'is-active' : ''}`}
              type="button"
              onClick={() => handleTabAction(tab)}
            >
              <Icon className="lg3-ico" />
              <span className="lg3-label">{tab.label}</span>
            </button>
          );
        })}
      </div>
    </nav>
  );
};
